import { Button, cn, toast } from "@cafe/ui";
import type { PlatformPermission } from "@cafe/shared";
import { BarChart3, Bell, BookOpenCheck, Building2, CalendarCheck, FileText, Gauge, GraduationCap, HandCoins, Inbox, LifeBuoy, LogOut, Megaphone, Package, ScrollText, Settings, Users, Wallet } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { NavLink, useNavigate } from "react-router";
import { auth, http, ROLES, useCan, useSession } from "./lib/api";
import { CocaLogo } from "./logo";
import { usePlatformEvent } from "./lib/realtime";

const NAV: { to: string; label: string; icon: typeof Gauge; perm: PlatformPermission }[] = [
  { to: "/", label: "Сводка", icon: Gauge, perm: "metrics.view" },
  { to: "/companies", label: "Компании", icon: Building2, perm: "companies.view" },
  { to: "/support", label: "Обращения", icon: LifeBuoy, perm: "support.view" },
  { to: "/leads", label: "Заявки", icon: Inbox, perm: "leads.view" },
  { to: "/broadcasts", label: "Рассылки", icon: Megaphone, perm: "broadcasts.view" },
  { to: "/learn", label: "Обучение", icon: GraduationCap, perm: "companies.view" },
  { to: "/sales", label: "Продажи", icon: BarChart3, perm: "billing.view" },
  { to: "/invoices", label: "Счета", icon: FileText, perm: "billing.view" },
  { to: "/schedule", label: "Табель оплат", icon: CalendarCheck, perm: "billing.view" },
  { to: "/money", label: "Денежные операции", icon: Wallet, perm: "billing.view" },
  { to: "/staff-finance", label: "Финансы сотрудников", icon: HandCoins, perm: "billing.view" },
  { to: "/catalog", label: "Тарифы и дополнения", icon: Package, perm: "companies.view" },
  { to: "/menu-packs", label: "Готовые меню", icon: BookOpenCheck, perm: "billing.view" },
  { to: "/staff", label: "Сотрудники", icon: Users, perm: "staff.manage" },
  { to: "/audit", label: "Журнал действий", icon: ScrollText, perm: "audit.view" },
  { to: "/notifications", label: "Уведомления", icon: Bell, perm: "metrics.view" },
  { to: "/settings", label: "Настройки", icon: Settings, perm: "companies.view" },
];

type SupportEvent = { id: string; number: number; subject: string; by: "user" | "platform" | "close" };

/** Обращения в реальном времени: очередь и открытая переписка обновляются сразу; о сообщении клиента — тост на любой странице. */
function useSupportEvents() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  usePlatformEvent<SupportEvent>("support.updated", ({ data }) => {
    void qc.invalidateQueries({ queryKey: ["support-list"] });
    void qc.invalidateQueries({ queryKey: ["support-summary"] });
    void qc.invalidateQueries({ queryKey: ["support", data.id] });
    if (data.by === "user") toast.info(`Новое сообщение: № ${data.number} · ${data.subject}`, { action: { label: "Открыть", onClick: () => navigate(`/support?ticket=${data.id}`) } });
  });
}

type LeadEvent = { id: string; venueName: string; name: string };

/** Новая заявка с сайта: тост на любой странице, очередь и счётчик обновляются. */
function useLeadEvents() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  usePlatformEvent<LeadEvent>("lead.created", ({ data }) => {
    void qc.invalidateQueries({ queryKey: ["leads"] });
    void qc.invalidateQueries({ queryKey: ["leads-summary"] });
    toast.info(`Новая заявка: ${data.venueName} · ${data.name}`, { action: { label: "Открыть", onClick: () => navigate(`/leads?lead=${data.id}`) } });
  });
}

/** Оболочка Platform Admin: тёмная боковая панель, контент справа; на телефоне — навигация сверху. */
export function Layout({ children }: { children: ReactNode }) {
  const s = useSession()!;
  const can = useCan();
  const items = NAV.filter((n) => can(n.perm));
  useSupportEvents();
  useLeadEvents();
  const leads = useQuery({ queryKey: ["leads-summary"], queryFn: () => http.get<{ new: number }>("/leads/summary"), enabled: can("leads.view"), refetchInterval: 120_000 });
  const badge: Record<string, number | undefined> = { "/leads": leads.data?.new };
  return (
    <div className="flex min-h-dvh flex-col bg-bg md:flex-row">
      <aside className="flex shrink-0 flex-col gap-1 bg-rail p-3 text-white md:w-60 md:p-4">
        <div className="mb-2 flex items-center justify-between md:mb-6 md:block">
          <p className="flex items-end gap-2"><CocaLogo className="h-8 w-auto" /><span className="pb-1 text-sm font-semibold text-white/70">Platform</span></p>
          <p className="hidden text-xs text-white/60 md:block">{s.user.name} · {ROLES[s.user.role] ?? s.user.role}</p>
        </div>
        <nav aria-label="Разделы" className="flex gap-1 overflow-x-auto md:flex-col">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"}
              className={({ isActive }) => cn("flex min-h-11 shrink-0 items-center gap-3 rounded-tile px-3 text-sm transition-colors", isActive ? "bg-primary text-white" : "text-white/75 hover:bg-white/10")}>
              <n.icon className="size-5" aria-hidden />{n.label}
              {!!badge[n.to] && <span className="ms-auto rounded-full bg-white/15 px-2 text-xs font-semibold tabular-nums" aria-label={`новых: ${badge[n.to]}`}>{badge[n.to]}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto hidden pt-4 md:block">
          <Button variant="secondary" size="sm" fullWidth leftIcon={<LogOut />} onClick={() => auth.set(null)}>Выйти</Button>
        </div>
      </aside>
      <main className="min-w-0 flex-1 p-4 sm:p-6">
        <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-5">{children}</div>
      </main>
    </div>
  );
}
